import React, { useState } from 'react';
import { Save, Edit, Trash2, AlertTriangle, FileText, X } from 'lucide-react';
import { useViaggi, useContatti, useMagazzini } from '../hooks/useSupabase';
import { getToday, formatDate, formatFullName } from '../utils/formatters';

const Viaggi = () => {
  const { viaggi, loading, error, addViaggio, updateViaggio, deleteViaggio } = useViaggi();
  const { clienti, vettori } = useContatti();
  const { magazzini } = useMagazzini();
  
  const [nuovoViaggio, setNuovoViaggio] = useState({
    id: '',
    data: getToday(),
    clienteId: '',
    vettoreId: '',
    magazzinoCarico: '',
    magazzinoScarico: '',
    prezzoCliente: '',
    prezzoVettore: '',
    bancali: '26',
    note: ''
  });
  
  const [modalitaModifica, setModalitaModifica] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [notaVisibile, setNotaVisibile] = useState(false);
  const [notaDettagli, setNotaDettagli] = useState({ titolo: '', contenuto: '' });

  // Funzione per aprire l'overlay della nota
  const apriNota = (viaggio) => {
    const titolo = `Note viaggio del ${formatDate(viaggio.data)}`;
    setNotaDettagli({ 
      titolo, 
      contenuto: viaggio.note
    });
    setNotaVisibile(true);
  };

  // Funzione per chiudere l'overlay
  const chiudiNota = () => {
    setNotaVisibile(false);
  };

  // Funzione di validazione dei campi
  const validaViaggio = () => {
    const errors = {};
    
    // La data è sempre obbligatoria
    if (!nuovoViaggio.data) {
      errors.data = 'La data è obbligatoria';
    }
    
    // Il cliente è obbligatorio
    if (!nuovoViaggio.clienteId) {
      errors.clienteId = 'Selezionare un cliente';
    }
    
    // Validazione dei valori numerici se presenti
    if (nuovoViaggio.prezzoCliente && (isNaN(nuovoViaggio.prezzoCliente) || parseFloat(nuovoViaggio.prezzoCliente) <= 0)) {
      errors.prezzoCliente = 'Il prezzo cliente deve essere un numero maggiore di zero';
    }
    
    if (nuovoViaggio.prezzoVettore && (isNaN(nuovoViaggio.prezzoVettore) || parseFloat(nuovoViaggio.prezzoVettore) <= 0)) {
      errors.prezzoVettore = 'Il prezzo vettore deve essere un numero maggiore di zero';
    }
    
    if (nuovoViaggio.bancali && (isNaN(nuovoViaggio.bancali) || parseInt(nuovoViaggio.bancali) <= 0)) {
      errors.bancali = 'Il numero di bancali deve essere un numero intero positivo';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const aggiungiViaggio = async () => {
    // Validazione dei campi prima del salvataggio
    if (!validaViaggio()) {
      // Mostra un messaggio di errore riepilogativo
      alert('Non è possibile salvare il viaggio: verificare i campi obbligatori evidenziati');
      return;
    }
    
    try {
      if (modalitaModifica) {
        await updateViaggio(nuovoViaggio);
        setModalitaModifica(false);
      } else {
        await addViaggio(nuovoViaggio);
      }
      
      // Reset form
      setNuovoViaggio({
        id: '',
        data: getToday(),
        clienteId: '',
        vettoreId: '',
        magazzinoCarico: '',
        magazzinoScarico: '',
        prezzoCliente: '',
        prezzoVettore: '',
        bancali: '26',
        note: ''
      });
      
      // Pulisci gli errori
      setValidationErrors({});
      setErrorMessage('');
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  };
  
  const eliminaViaggio = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo viaggio? Questa operazione non può essere annullata.')) return;
    
    try {
      await deleteViaggio(id);
      setErrorMessage('');
    } catch (err) {
      alert(`Errore durante l'eliminazione del viaggio: ${err.message}`);
      setErrorMessage('');
    }
  };
  
  const modificaViaggio = (viaggio) => {
    setNuovoViaggio(viaggio);
    setModalitaModifica(true);
    setValidationErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Funzione per trovare il nome del contatto
  const trovaContatto = (id) => {
    const contatto = [...clienti, ...vettori].find(c => c.id === id);
    if (!contatto) return '';
    return formatFullName(contatto.nome, contatto.cognome, contatto.azienda);
  };

  // Funzione per trovare il nome del magazzino
  const trovaMagazzino = (id) => {
    const magazzino = magazzini.find(m => m.id === id);
    return magazzino ? magazzino.nome : '';
  };

  // Funzione per generare la classe CSS per gli input con errori
  const getInputClass = (fieldName) => {
    return validationErrors[fieldName] 
      ? "w-full p-2 border rounded border-red-500 bg-red-50"
      : "w-full p-2 border rounded";
  };

  // Funzione per formattare l'ID in modo più leggibile
  const formatUUID = (uuid) => {
    if (!uuid) return '';
    // Prendi solo gli ultimi 8 caratteri dell'UUID per maggiore leggibilità
    return uuid.substring(uuid.length - 4);
  };

  if (loading) return <div className="text-center py-8">Caricamento in corso...</div>;
  
  // Ignoriamo specificamente gli errori di foreign key constraint che abbiamo già gestito
  if (error && !error.includes('violates foreign key constraint')) {
    return <div className="text-center py-8 text-red-600">Errore: {error}</div>;
  }
  
  if (errorMessage) return <div className="text-center py-8 text-red-600">{errorMessage}</div>;

  return (
    <>
      {/* Form Gestione Viaggi */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-xl font-semibold mb-4">{modalitaModifica ? 'Modifica Viaggio' : 'Nuovo Viaggio'}</h2>
        
        {/* Avviso di compilazione campi obbligatori */}
        {Object.keys(validationErrors).length > 0 && (
          <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start">
            <AlertTriangle className="mr-2 flex-shrink-0 mt-1" size={20} />
            <div>
              <p className="font-bold">Attenzione</p>
              {validationErrors.generale ? (
                <p>{validationErrors.generale}</p>
              ) : (
                <p>Correggere gli errori evidenziati prima di salvare il viaggio.</p>
              )}
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Prima riga: Data e Bancali */}
          <div>
            <label className="block text-gray-700 mb-1">Data *</label>
            <input 
              type="date" 
              className={getInputClass('data')}
              value={nuovoViaggio.data}
              onChange={e => setNuovoViaggio({...nuovoViaggio, data: e.target.value})}
            />
            {validationErrors.data && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.data}</p>
            )}
          </div>
          
          <div>
            <label className="block text-gray-700 mb-1">PLT</label>
            <input 
              type="number" 
              className={getInputClass('bancali')}
              value={nuovoViaggio.bancali}
              onChange={e => setNuovoViaggio({...nuovoViaggio, bancali: e.target.value})}
              min="1"
              step="1"
            />
            {validationErrors.bancali && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.bancali}</p>
            )}
          </div>
          
          {/* Seconda riga: Cliente e Vettore */}
          <div>
            <label className="block text-gray-700 mb-1">Cliente *</label>
            <select 
              className={getInputClass('clienteId')}
              value={nuovoViaggio.clienteId}
              onChange={e => setNuovoViaggio({...nuovoViaggio, clienteId: e.target.value})}
            >
              <option value="">Seleziona cliente</option>
              {clienti.map(cliente => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.azienda} ({cliente.nome} {cliente.cognome})
                </option>
              ))}
            </select>
            {validationErrors.clienteId && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.clienteId}</p>
            )}
          </div>
          
          <div>
            <label className="block text-gray-700 mb-1">Vettore</label>
            <select 
              className={getInputClass('vettoreId')}
              value={nuovoViaggio.vettoreId}
              onChange={e => setNuovoViaggio({...nuovoViaggio, vettoreId: e.target.value})}
            >
              <option value="">Seleziona vettore</option>
              {vettori.map(vettore => (
                <option key={vettore.id} value={vettore.id}>
                  {vettore.azienda} ({vettore.nome} {vettore.cognome})
                </option>
              ))}
            </select>
            {validationErrors.vettoreId && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.vettoreId}</p>
            )}
          </div>
          
          {/* Terza riga: Prezzo Cliente e Prezzo Vettore */}
          <div>
            <label className="block text-gray-700 mb-1">Prezzo Cliente (€)</label>
            <input 
              type="number" 
              className={getInputClass('prezzoCliente')}
              value={nuovoViaggio.prezzoCliente}
              onChange={e => setNuovoViaggio({...nuovoViaggio, prezzoCliente: e.target.value})}
              min="0"
              step="0.01"
            />
            {validationErrors.prezzoCliente && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.prezzoCliente}</p>
            )}
          </div>
          
          <div>
            <label className="block text-gray-700 mb-1">Prezzo Vettore (€)</label>
            <input 
              type="number" 
              className={getInputClass('prezzoVettore')}
              value={nuovoViaggio.prezzoVettore}
              onChange={e => setNuovoViaggio({...nuovoViaggio, prezzoVettore: e.target.value})}
              min="0"
              step="0.01"
            />
            {validationErrors.prezzoVettore && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.prezzoVettore}</p>
            )}
          </div>
          
          {/* Quarta riga: Magazzini */}
          <div>
            <label className="block text-gray-700 mb-1">Magazzino di Carico</label>
            <select 
              className={getInputClass('magazzinoCarico')}
              value={nuovoViaggio.magazzinoCarico}
              onChange={e => setNuovoViaggio({...nuovoViaggio, magazzinoCarico: e.target.value})}
            >
              <option value="">Seleziona magazzino</option>
              {magazzini.map(magazzino => (
                <option key={magazzino.id} value={magazzino.id}>{magazzino.nome}</option>
              ))}
            </select>
            {validationErrors.magazzinoCarico && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.magazzinoCarico}</p>
            )}
          </div>
          
          <div>
            <label className="block text-gray-700 mb-1">Magazzino di Scarico</label>
            <select 
              className={getInputClass('magazzinoScarico')}
              value={nuovoViaggio.magazzinoScarico}
              onChange={e => setNuovoViaggio({...nuovoViaggio, magazzinoScarico: e.target.value})}
            >
              <option value="">Seleziona magazzino</option>
              {magazzini.map(magazzino => (
                <option key={magazzino.id} value={magazzino.id}>{magazzino.nome}</option>
              ))}
            </select>
            {validationErrors.magazzinoScarico && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.magazzinoScarico}</p>
            )}
          </div>
          
          {/* Quinta riga: Note (span 2 colonne) */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 mb-1">Note</label>
            <textarea 
              className={getInputClass('note')}
              value={nuovoViaggio.note || ''}
              onChange={e => setNuovoViaggio({...nuovoViaggio, note: e.target.value})}
              rows="3"
              placeholder="Inserisci eventuali note sul viaggio..."
            ></textarea>
            {validationErrors.note && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.note}</p>
            )}
          </div>
        </div>
        
        <button 
          className="bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
          onClick={aggiungiViaggio}
        >
          <Save size={20} className="mr-2" />
          {modalitaModifica ? 'Aggiorna Viaggio' : 'Salva Viaggio'}
        </button>
        
        {modalitaModifica && (
          <button 
            className="bg-gray-500 text-white px-4 py-2 rounded ml-2 inline-flex items-center"
            onClick={() => {
              setModalitaModifica(false);
              setNuovoViaggio({
                id: '',
                data: getToday(),
                clienteId: '',
                vettoreId: '',
                magazzinoCarico: '',
                magazzinoScarico: '',
                prezzoCliente: '',
                prezzoVettore: '',
                bancali: '26',
                note: ''
              });
              setValidationErrors({});
            }}
          >
            Annulla
          </button>
        )}
      </div>
      
      {/* Lista Viaggi */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Lista Viaggi</h2>
        
        {viaggi.length === 0 ? (
          <p className="text-gray-500">Nessun viaggio registrato</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-2 text-center w-16">ID</th>
                  <th className="py-2 px-2 text-left w-24">Data</th>
                  <th className="py-2 px-2 text-left w-56">Cliente</th>
                  <th className="py-2 px-2 text-left w-56">Vettore</th>
                  <th className="py-2 px-2 text-right w-28">Prezzo C.</th>
                  <th className="py-2 px-2 text-right w-28">Prezzo V.</th>
                  <th className="py-2 px-2 text-right w-16">PLT</th>
                  <th className="py-2 px-2 text-left w-40">Carico</th>
                  <th className="py-2 px-2 text-left w-40">Scarico</th>
                  <th className="py-2 px-2 text-center w-16">Note</th>
                  <th className="py-2 px-2 text-center w-20">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {viaggi.map((viaggio) => (
                  <tr key={viaggio.id} className="border-t hover:bg-gray-50">
                    <td className="py-2 px-2 text-center">{formatUUID(viaggio.id)}</td>
                    <td className="py-2 px-2">{formatDate(viaggio.data)}</td>
                    <td className="py-2 px-2 truncate">{trovaContatto(viaggio.clienteId)}</td>
                    <td className="py-2 px-2 truncate">{trovaContatto(viaggio.vettoreId)}</td>
                    <td className="py-2 px-2 text-right">{viaggio.prezzoCliente}</td>
                    <td className="py-2 px-2 text-right">{viaggio.prezzoVettore}</td>
                    <td className="py-2 px-2 text-right">{viaggio.bancali}</td>
                    <td className="py-2 px-2 truncate">{trovaMagazzino(viaggio.magazzinoCarico)}</td>
                    <td className="py-2 px-2 truncate">{trovaMagazzino(viaggio.magazzinoScarico)}</td>
                    <td className="py-2 px-2 text-center">
                      {viaggio.note ? (
                        <button
                          className="text-gray-600 hover:text-blue-800"
                          onClick={() => apriNota(viaggio)}
                          title="Visualizza nota"
                        >
                          <FileText size={16} />
                        </button>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-center">
                      <button 
                        className="text-blue-600 hover:text-blue-800 mr-1"
                        onClick={() => modificaViaggio(viaggio)}
                        title="Modifica"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-800"
                        onClick={() => eliminaViaggio(viaggio.id)}
                        title="Elimina"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Overlay per le note */}
      {notaVisibile && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-3/4 flex flex-col">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">{notaDettagli.titolo}</h3>
              <button 
                onClick={chiudiNota}
                className="text-gray-500 hover:text-gray-700 p-2 rounded hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="whitespace-pre-wrap text-gray-700">{notaDettagli.contenuto}</div>
            </div>
            
            <div className="p-4 border-t">
              <button 
                onClick={chiudiNota}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Viaggi;